import { useEffect, useRef, useState } from "react";
import { initializeApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useFirebase } from "app/firebase/context";

// ===================================================================
//  MODO DE DEPURACIÓN
//  Poner a 'true' para registrar todos los eventos de UJET en la consola.
//  Poner a 'false' para el funcionamiento normal.
// ===================================================================
const DEBUG_MODE = true;

// Configuración Firebase
const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  databaseURL:  process.env.REACT_APP_DATABASE_URL,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID,
};

const generateUUID = () =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

export const useUJET = ({ user = null, currdb = null }) => {
  const [chatId, setChatId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const ujetInstanceRef = useRef(null);
  const firebaseAppRef = useRef(null);
  const scriptRef = useRef(null);
  const firebase = useFirebase();

  const cleanupUJET = async () => {
    try {
      if (ujetInstanceRef.current?.cleanup) ujetInstanceRef.current.cleanup();
      if (ujetInstanceRef.current?.destroy) ujetInstanceRef.current.destroy();
      ujetInstanceRef.current = null;
      const script = document.querySelector('script[src*="ujet"]');
      if (script?.parentNode) script.parentNode.removeChild(script);
      if (window.UJET) {
        try {
          delete window.UJET;
        } catch (e) {
          window.UJET = undefined;
        }
      }
    } catch (e) {
      console.warn("UJET cleanup falló:", e);
    }
  };

  const loadUJET = () => {
    return new Promise((resolve, reject) => {
      if (window.UJET) return resolve();
      const script = document.createElement("script");
      script.src = "https://websdk.ujet.co/v2/loader.js";
      script.async = true;
      script.onload = () => (window.UJET ? resolve() : reject(new Error("UJET no disponible")));
      script.onerror = () => reject(new Error("Script de UJET falló al cargar"));
      document.head.appendChild(script);
      scriptRef.current = script;
    });
  };

  const getAuthTokenAutenticado = () => {
    const functions = getFunctions(firebase.app, "europe-west1");
    const getCCAIToken = httpsCallable(functions, "getCCAIToken");
    return getCCAIToken({
      payload: {
        identifier: user.uid,
        name: user.displayName,
        email: user.email,
        basedatosactual: user.currentDB,
        urlcabecera: window.location.origin,
      },
    })
      .then((res) => ({
        token: res.data,
        user: { identifier: user.uid, name: user.displayName, email: user.email },
      }))
      .catch((e) => console.log("La obtención del token autenticado falló", e));
  };

  const getAuthToken = async (functions, identifier) => {
    const getCCAITokenUsuarioInvitado = httpsCallable(functions, "getCCAITokenComercial");
    const { data: token } = await getCCAITokenUsuarioInvitado({ payload: { identifier } });
    return {
      token,
      user: { identifier, name: "Usuario sin registrar", email: "usuariosinregistrar@gmail.com" },
    };
  };

  // Función para registrar los listeners de eventos
  const registerEventListeners = (ujetInstance) => {
    if (DEBUG_MODE) {
      // --- MODO DEBUG: Registra todo ---
      console.warn("UJET en modo DEBUG. Se registrarán todos los eventos.");
      const eventosAProbar = [
        "conversationStarted",
        "conversation:started",
        "chat:started",
        "session:started",
        "created",
        "ready",
        "open",
        "close",
        "authenticated",
        "updated",
        "chat:updated",
        "chat:connected",
        "chat:ongoing",
        "loading",
        "agent-send-message",
        "visitor-send-message",
      ];

      eventosAProbar.forEach((nombreEvento) => {
        const handler = (data) => {
          console.log(`✅ EVENTO (DEBUG) [${nombreEvento}]:`, data);
          if (data && data.id && !chatId) setChatId(data.id);
          if (data && data.session_id && !sessionId) setSessionId(data.session_id);
        };
        try {
          ujetInstance.registerHook(nombreEvento, handler);
        } catch (e) {
          /* Ignorar si el hook no existe */
        }
        try {
          ujetInstance.on(nombreEvento, handler);
        } catch (e) {
          /* Ignorar si el evento no existe */
        }
      });
    } else {
      // --- MODO NORMAL: Solo listeners optimizados ---
      ujetInstance.on("updated", function (data) {
        if (data && data.session_id && sessionId !== data.session_id) {
          console.log("UJET: Session ID capturado ->", data.session_id);
          setSessionId(data.session_id);
        }
      });
      ujetInstance.registerHook("conversationStarted", function (e) {
        if (e && e.id && chatId !== e.id) {
          console.log("UJET: Chat ID capturado ->", e.id);
          setChatId(e.id);
        }
      });
    }

    // El evento 'created' es fundamental para iniciar, se registra siempre
    return (token) => {
      ujetInstance.on("created", function () {
        ujetInstance.start({ menuKey: "Soporte" });
        ujetInstance.authenticate(token);
      });
    };
  };

  const initUJET = async () => {
    try {
      await cleanupUJET();
      await loadUJET();
      if (!window.UJET) return;

      if (user) {
        // --- LÓGICA PARA USUARIO AUTENTICADO ---
        ujetInstanceRef.current = new window.UJET({
          logo: "https://conecta-c365.firebaseapp.com/assets/images/Imagenes/logo_app_transparente.png",
          right: "50px",
          bottom: "150px",
          companyId: "17139048722842872cb3d56ef53938e3a",
          lang: "es",
          host: "https://jorge-wpdyft6.ew1.ccaiplatform.com",
          customData: {
            basedatosactual: { label: "basedatosactual", value: user.currentDB },
            urlcabecera: { label: "urlcabecera", value: window.location.origin },
          },
          user: { identifier: user.uid, name: user.displayName, email: user.email },
        });

        const eltoken = await getAuthTokenAutenticado();
        const startUjet = registerEventListeners(ujetInstanceRef.current);
        startUjet(eltoken);
      } else {
        // --- LÓGICA PARA INVITADO ---
        const identifier = generateUUID();
        if (!firebaseAppRef.current) {
          firebaseAppRef.current = initializeApp(firebaseConfig, "nueva");
        }
        const functions = getFunctions(firebaseAppRef.current, "europe-west1");
        const eltoken = await getAuthToken(functions, identifier);

        ujetInstanceRef.current = new window.UJET({
          logo: "https://www.conecta365.es/assets/images/cafportal.png",
          right: "50px",
          bottom: "150px",
          companyId: "17139048722842872cb3d56ef53938e3a",
          lang: "es",
          host: "https://jorge-wpdyft6.ew1.ccaiplatform.com",
        });

        const startUjet = registerEventListeners(ujetInstanceRef.current);
        startUjet(eltoken);
      }
    } catch (err) {
      console.error("UJET error:", err);
    }
  };

  useEffect(() => {
    initUJET();
    return () => {
      cleanupUJET();
    };
  }, [user]);

  const resetUJET = () => {
    cleanupUJET();
  };

  return { resetUJET, chatId, sessionId };
};
